import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MUIDataTable, { MUIDataTableOptions, MUIDataTableState } from 'mui-datatables';

import { KeyboardBackspaceOutlined } from '@mui/icons-material';
import { Pagination, PaginationItem, TableCell, TableFooter, TablePagination, TableRow } from '@mui/material';

import { useAuthPermission } from '@src/hooks/useAuthPermission';
import { useGetTheme } from '@src/hooks/useGetTheme';
import { capitalizeFirstLetter } from '@src/lib/helper';

import { Field } from '../FormController/type';

import { ActionMenu } from './ActionMenu';
import TableHeaderLayout from './CustomTableToolbar';
import { StyledFilterOption, StyledPageButtons, StyledStatusFilterContainer } from './style';
import {
  AddRowHandleProps,
  AddRowProps,
  ButtonConditions,
  DataTableProps,
  handleApplyColumnFilterType,
  handleFetchDataProps,
  TableDataType,
} from './type';

const Table = ({
  data: providedData = [],
  headers,
  count: providedCount,
  title = '',
  handleFetchData,
  statusFilters,
  handleUploadFile,
  tableActions,
  handleTableRowClick,
  addRowData,
  filterSchema,
  downloadSchemaLinks,
  tableOptions,
  isUserSynUp,
  getRefetch,
  ctaButton,
}: DataTableProps) => {
  const isAuthorized = useAuthPermission();
  const { styledTheme } = useGetTheme();
  const { t } = useTranslation();

  const [count, setCount] = useState<number>();
  const [data, setData] = useState<TableDataType>([]);
  const [sortBy, setSortBy] = useState<{ sort_by: string; order_by: number }>();
  const [searchText, setSearchText] = useState<string>('');

  const [filterValues, setFilterValues] = useState<Record<string, string>>({
    status: statusFilters?.statusOptions[0] || '',
  });

  const { idKey, handleDelete, handleEdit, handleView, deleteMessage } = tableActions ?? {};
  const { handleAddClick } = (addRowData as AddRowHandleProps) || {};
  const initialColumns = headers.filter(item => !item.hideInitially);
  const [filteredHeaders, setFilteredHeaders] = useState(initialColumns);

  const addRowDetails: AddRowProps = {
    ...addRowData,
    fields: (addRowData as AddRowProps)?.fields as Field[],
    onSubmit: async submitData => {
      await (addRowData as AddRowProps)?.onSubmit?.(submitData);
      fetchData({ rowsPerPage: 10, page: 0, searchText, filters: filterValues, sortBy: sortBy });
    },
  };

  const fetchData = useCallback(
    async ({ rowsPerPage, page, searchText: text, filters, sortBy: sortByFilter }: handleFetchDataProps) => {
      const res = await handleFetchData?.({
        rowsPerPage,
        page: page + 1,
        searchText: text,
        filters,
        sortBy: sortByFilter,
      });

      setData([...(res?.data || [])]);
      setCount(res?.count);
    },
    [handleFetchData]
  );

  const handleTableChange = async (action: string, tableState: MUIDataTableState) => {
    if (['changePage', 'changeRowsPerPage'].includes(action))
      await fetchData?.({ ...tableState, searchText, filters: filterValues, sortBy });
  };

  const handleApplyColumnFilter: handleApplyColumnFilterType = selectedColumns => {
    if (selectedColumns === 'reset') return setFilteredHeaders(initialColumns);

    const newSelectedColumns = headers.filter(header => selectedColumns[header.name]);
    setFilteredHeaders(newSelectedColumns);
  };

  const handleStatusFilter = (statusKey: string, item: string) => {
    setFilterValues({ ...filterValues, [statusKey]: item });
  };

  useEffect(() => {
    if (!handleFetchData) return;

    fetchData({ rowsPerPage: 10, page: 0, searchText, filters: filterValues, sortBy });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValues, searchText, sortBy]);

  const handleDataFileUpload = async (file: FormData) => {
    try {
      await handleUploadFile?.(file);
      fetchData({ rowsPerPage: 10, page: 0, searchText, filters: filterValues, sortBy });
    } catch (error) {
      console.log('Error', error);
    }
  };

  const handleRowClick = (rowData: string[], rowMeta: { dataIndex: number; rowIndex: number }) => {
    const { rowIndex } = rowMeta;
    const row = data[rowIndex] as Record<string, string>;
    const id = idKey ? row[idKey] : row.id;

    if (handleTableRowClick) {
      handleTableRowClick(id);
    }
  };

  const defaultOptions: MUIDataTableOptions = {
    serverSide: true,
    selectableRows: 'none',
    search: false,
    download: false,
    print: false,
    viewColumns: false,
    filter: false,
    rowsPerPageOptions: [5, 10, 25, 50, 100],
    tableBodyHeight: '49vh',
    count: providedCount || count,
    onTableChange: handleTableChange,
    onRowClick: handleRowClick,
    customFooter: (currentCount, page, rowsPerPage, changeRowsPerPage, changePage) => {
      const numPages = Math.ceil(currentCount / rowsPerPage);

      return (
        <TableFooter>
          <TableRow>
            <TableCell style={{ display: 'flex', justifyContent: 'space-between', padding: 0, alignItems: 'center' }}>
              <TablePagination
                component='div'
                count={currentCount}
                page={page}
                onPageChange={(event, newPage) => changePage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={event => changeRowsPerPage(parseInt(event.target.value, 10))}
                labelRowsPerPage='Rows per page:'
                labelDisplayedRows={() => null}
                sx={{
                  '& .MuiTablePagination-actions': {
                    display: 'none',
                  },
                }}
                style={{
                  color: styledTheme.colors.charcoalMist,
                  fontFamily: 'Inter',
                  fontSize: styledTheme.newSpace.get('m'),
                  fontWeight: 500,
                }}
              />

              <Pagination
                count={numPages}
                page={page + 1}
                onChange={(event, value) => changePage(value - 1)}
                shape='rounded'
                renderItem={item => (
                  <PaginationItem
                    {...item}
                    slots={{
                      previous: () => (
                        <StyledPageButtons>
                          <KeyboardBackspaceOutlined fontSize='small' />
                          Previous
                        </StyledPageButtons>
                      ),
                      next: () => (
                        <StyledPageButtons>
                          Next
                          <KeyboardBackspaceOutlined fontSize='small' style={{ transform: 'rotate(180deg)' }} />
                        </StyledPageButtons>
                      ),
                    }}
                    style={{
                      color: styledTheme.colors.midnightGray,
                      fontFamily: 'Inter',
                      fontSize: styledTheme.newSpace.get('m'),
                      fontWeight: 500,
                    }}
                  />
                )}
              />
            </TableCell>
          </TableRow>
        </TableFooter>
      );
    },
    textLabels: {
      body: {
        noMatch: t('noDataFound'),
      },
    },
    onColumnSortChange: (changedColumn: string, direction: 'asc' | 'desc') => {
      setSortBy({
        sort_by: changedColumn,
        order_by: direction === 'asc' ? 1 : -1,
      });
    },
    ...tableOptions,
  };

  const refetch = () => {
    fetchData({ rowsPerPage: 10, page: 0, searchText, filters: filterValues, sortBy: sortBy });
  };

  useEffect(() => {
    if (!getRefetch) return;

    getRefetch(refetch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getRefetch]);

  if (
    (handleDelete || handleEdit || handleView) &&
    !filteredHeaders.some(({ name }) => name === 'action') &&
    data.length
  ) {
    filteredHeaders.push({
      label: 'action',
      name: 'action',
      options: {
        customBodyRender: (_, { rowIndex }) => {
          const rowData = data[rowIndex] as Record<string, string>;

          return (
            <ActionMenu
              id={idKey ? rowData?.[idKey] : rowData?.id}
              handleView={handleView}
              deleteMessage={deleteMessage}
              refetch={refetch}
              {...getHasEditDelete(rowData)}
            />
          );
        },
      },
    });
  }

  const getHasEditDelete = (rowData: Record<string, string>) => {
    const obj: Record<string, ((id: string) => void) | string> = {};
    const hasEditButton = handleEdit && [true, 'write', 'delete'].includes(isAuthorized);
    const hasDeleteButton = handleDelete && [true, 'delete'].includes(isAuthorized);

    if (hasEditButton) {
      if (checkAllConditions(rowData, tableActions?.editButtonConditions)) {
        obj['handleEdit'] = handleEdit;
      }
    }

    if (hasDeleteButton) {
      if (checkAllConditions(rowData, tableActions?.deleteButtonConditions)) {
        obj['handleDelete'] = handleDelete;
      }

      if (tableActions?.deleteTextConditions) {
        const conditionsPassed = checkAllConditions(rowData, [tableActions.deleteTextConditions]);

        if (conditionsPassed) {
          obj['deleteText'] = tableActions.deleteTextConditions.matchText;

          if (tableActions.deleteTextConditions.overrideMatchMessage) {
            obj['overrideDeleteMessage'] = tableActions.deleteTextConditions.overrideMatchMessage;
          }
        } else {
          obj['deleteText'] = tableActions.deleteTextConditions.noMatchText;

          if (tableActions.deleteTextConditions.overrideNoMatchMessage) {
            obj['overrideDeleteMessage'] = tableActions.deleteTextConditions.overrideNoMatchMessage;
          }
        }
      }
    }

    return obj;
  };

  const checkAllConditions = (rowData: Record<string, string | boolean>, conditions?: ButtonConditions[]) => {
    if (!conditions) return true;

    let checksOut = true;

    for (const condition of conditions) {
      const { key, shouldNotBe, value } = condition;
      const isEqual = rowData?.[key] === value;

      if ((shouldNotBe && isEqual) || (!shouldNotBe && !isEqual)) {
        checksOut = false;
      }
    }

    return checksOut;
  };

  useEffect(() => {
    setFilteredHeaders(
      headers.filter(item => {
        return !item.hideInitially && item.label !== 'status';
      })
    );
  }, [headers, data]);

  return (
    <>
      {statusFilters?.statusKey ? (
        <StyledStatusFilterContainer>
          {statusFilters?.statusOptions.map(item => {
            return (
              <StyledFilterOption
                statusSelected={filterValues?.[statusFilters?.statusKey] === item}
                key={item}
                onClick={() => handleStatusFilter(statusFilters?.statusKey, item)}
              >
                {capitalizeFirstLetter(item)}
              </StyledFilterOption>
            );
          })}
        </StyledStatusFilterContainer>
      ) : null}
      <TableHeaderLayout
        setSearchText={setSearchText}
        headers={headers}
        handleApplyColumnFilter={handleApplyColumnFilter}
        isUserSynUp={isUserSynUp}
        {...(ctaButton ? { ctaButton } : {})}
        {...(handleUploadFile ? { handleUploadFile: handleDataFileUpload } : {})}
        {...([true, 'write', 'delete'].includes(isAuthorized) && addRowData
          ? { addRowData: { ...addRowDetails, handleAddClick } }
          : {})}
        applyFilters={filters => {
          setFilterValues(filters);
        }}
        filterCount={Object.keys(filterValues).length}
        filterSchema={filterSchema}
        downloadSchemaLinks={downloadSchemaLinks}
      >
        <MUIDataTable
          title=''
          data={providedData.length ? providedData : data}
          columns={filteredHeaders.map(header => ({
            ...header,
            ...(header?.label ? { label: t(header?.label || '') } : {}),
          }))}
          options={defaultOptions}
        />
      </TableHeaderLayout>
    </>
  );
};

export default Table;
